// Utility to help identify and clean up orphaned Firebase Auth users
// This can be used to find users that exist in Firebase Auth but not in Firestore

import { db } from '../firebaseConfig';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';

/**
 * Check if a user exists in the Firestore users collection
 * @param {string} email - The email to check
 * @returns {Promise<boolean>} - True if user exists in Firestore
 */
export async function checkUserExistsInFirestore(email) {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email.toLowerCase().trim()));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking user in Firestore:', error);
    return false;
  }
}

/**
 * Get user data from Firestore by email
 * @param {string} email - The email to look up
 * @returns {Promise<Object|null>} - User data or null if not found
 */
export async function getUserFromFirestore(email) {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email.toLowerCase().trim()));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const userDoc = querySnapshot.docs[0];
    return {
      id: userDoc.id,
      ...userDoc.data()
    };
  } catch (error) {
    console.error('Error getting user from Firestore:', error);
    return null;
  }
}

/**
 * Check if a user exists in Firestore by UID
 * @param {string} uid - The Firebase Auth UID to check
 * @returns {Promise<Object|null>} - User data or null if not found
 */
export async function getUserByUid(uid) {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      return {
        id: userDoc.id,
        ...userDoc.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting user by UID:', error);
    return null;
  }
}

/**
 * Debug function to help identify potential issues
 * @param {string} email - The email to debug
 */
export async function debugUser(email) {
  console.log(`\n=== Debugging User: ${email} ===`);
  
  const firestoreUser = await getUserFromFirestore(email);
  console.log('Firestore user:', firestoreUser);
  
  if (firestoreUser) {
    console.log('✅ User exists in Firestore users collection');
    console.log('User data:', {
      uid: firestoreUser.uid,
      email: firestoreUser.email,
      firstName: firestoreUser.firstName,
      surname: firestoreUser.surname,
      role: firestoreUser.role,
      status: firestoreUser.status
    });
  } else {
    console.log('❌ User does NOT exist in Firestore users collection');
    console.log('This might be an orphaned Firebase Auth user');
  }
  
  console.log('=== End Debug ===\n');
}









